"use client"

import posthog, {
  type BeforeSendFn,
  type CapturedNetworkRequest,
} from "posthog-js"
import { PostHogProvider as PHProvider } from "posthog-js/react"
import { usePathname } from "next/navigation"
import { useEffect } from "react"
import { filterPosthogEvent } from "./before-send"
import { normalizePathPattern } from "./telemetry"

const POSTHOG_URL_PROPERTIES = [
  "$current_url",
  "$initial_current_url",
  "$initial_pathname",
  "$pathname",
  "$prev_pageview_pathname",
  "$referrer",
  "$initial_referrer",
  "$session_entry_pathname",
  "$session_entry_referrer",
  "$session_entry_url",
  "$external_click_url",
  "href",
  "attr__href",
]

const POSTHOG_EXTERNAL_URL_PROPERTIES = new Set([
  "$referrer",
  "$initial_referrer",
  "$session_entry_referrer",
])

const SENSITIVE_ANALYTICS_KEY_RE =
  /(^|_)(address|answer|body|candidate_name|content|email|first_name|last_name|message|name|note|notes|org_name|org_slug|password|phone|query|response|resume|search|secret|title|token|transcript)(_|$)/i
// `query_key_*` matches the regex above via `^query_`. queryKeyTelemetry and
// mutationKeyTelemetry already scrub ids to ":id" and free text to "[set]".
const SAFE_DERIVED_KEYS = new Set([
  "candidate_type",
  // Error-boundary captures. The values still pass through
  // sanitizeStringValue (email/phone/length checks).
  "error_message",
  "error_name",
  "error_stack",
  "has_phone",
  "message_count",
  "message_kind",
  "message_length_bucket",
  "mutation_key_depth",
  "mutation_key_family",
  "mutation_key_shape",
  "query_key_depth",
  "query_key_family",
  "query_key_shape",
  "response_time_bucket",
])
const AUTOCAPTURE_PAYLOAD_KEYS = new Set([
  "$element_text",
  "$elements",
  "$elements_chain",
])
const EMAIL_VALUE_RE = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/i
const PHONE_VALUE_RE = /(?:\+?\d[\s().-]*){7,}/
// UUIDs (Supabase user ids, PostHog device/session ids) can contain 7+ digit
// runs and must never be treated as phone numbers: redacting `distinct_id`
// collapses every affected user into one shared "[redacted]" person.
const UUID_VALUE_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const PRIVACY_REPLAY_SAMPLE_KEY = "ha-posthog-replay-sampled-v1"
const PRIVACY_REPLAY_SAMPLE_RATE = 0.1

// PostHog promotes these query parameters into standalone event/session
// properties. Their values are uncontrolled and may contain candidate PII, so
// the application records only its own bounded `acquisition_source` category.
const POSTHOG_CAMPAIGN_PROPERTIES = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gad_source",
  "mc_cid",
  "gclid",
  "gclsrc",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "igshid",
  "ttclid",
  "rdt_cid",
  "epik",
  "qclid",
  "sccid",
  "irclid",
  "_kx",
])

function isRawCampaignProperty(key: string): boolean {
  const normalized = key
    .replace(/^\$session_entry_/, "")
    .replace(/^\$initial_/, "")
    .replace(/^\$/, "")
  return POSTHOG_CAMPAIGN_PROPERTIES.has(normalized)
}

function stripUrlQueryAndFragment(value: string): string {
  const queryIndex = value.indexOf("?")
  const hashIndex = value.indexOf("#")
  const cutAt = [queryIndex, hashIndex]
    .filter((index) => index >= 0)
    .reduce((earliest, index) => Math.min(earliest, index), Infinity)
  return Number.isFinite(cutAt) ? value.slice(0, cutAt) : value
}

function privacySafePath(pathname: string): string {
  const jobPath = pathname
    .replace(
      /^\/(embed\/)?jobs\/[^/]+\/[^/]+/,
      (_match, embed: string | undefined) =>
        `/${embed ?? ""}jobs/:org/:id`
    )
    .replace(/^\/(embed\/)?jobs\/[^/]+$/, "/$1jobs/:org")
  return normalizePathPattern(jobPath) ?? "/"
}

function privacySafeUrl(value: string, externalOnly = false): string {
  const stripped = stripUrlQueryAndFragment(value)
  try {
    const parsed = new URL(stripped, "https://app.local")
    const isRelative = !/^[a-z][a-z\d+.-]*:/i.test(stripped)
    if (externalOnly && !isRelative) return parsed.origin
    if (!isRelative && stripped === parsed.origin) return parsed.origin
    const safePath = privacySafePath(parsed.pathname)
    return isRelative ? safePath : `${parsed.origin}${safePath}`
  } catch {
    return privacySafePath(stripped)
  }
}

function sanitizeStringValue(value: string): string {
  if (UUID_VALUE_RE.test(value)) return value
  if (EMAIL_VALUE_RE.test(value) || PHONE_VALUE_RE.test(value)) {
    return "[redacted]"
  }
  if (/^(?:https?:\/\/|\/)/i.test(value)) return privacySafeUrl(value)
  if (value.length > 160 || value.includes("\n")) return "[redacted]"
  return value
}

// ApiError and ApiNetworkError encode their endpoint in the message as
// `[<status> <path>]` and `[network <method> <path>]`. Parse that into a
// privacy-safe, endpoint-scoped label, or return null for a non-API error.
function apiFailureSignature(rawValue: string): string | null {
  const match =
    rawValue.match(/\[(\d{3})\s+([^\]]+)\]/) ??
    rawValue.match(/\[(network\s+[A-Z]+)\s+([^\]]+)\]/)
  return match ? `[${match[1]} ${privacySafePath(match[2])}]` : null
}

// Grouping key for API failures. Error tracking otherwise fingerprints on the
// stack, whose per-deploy chunk hash mints a fresh issue every deploy and
// re-splits one fault. The endpoint signature is stable across deploys.
function apiFailureFingerprint(exceptionList: unknown): string | null {
  if (!Array.isArray(exceptionList)) return null
  for (const item of exceptionList) {
    const value =
      item &&
      typeof item === "object" &&
      typeof (item as Record<string, unknown>).value === "string"
        ? ((item as Record<string, unknown>).value as string)
        : ""
    const signature = apiFailureSignature(value)
    if (signature) return signature
  }
  return null
}

function sanitizeExceptionList(value: unknown): unknown {
  if (!Array.isArray(value)) return []
  return value.map((item) => {
    if (!item || typeof item !== "object") return { type: "Error", value: "[Error]" }
    const exception = item as Record<string, unknown>
    const type =
      typeof exception.type === "string" && exception.type.length <= 80
        ? exception.type
        : "Error"
    const rawValue = typeof exception.value === "string" ? exception.value : ""
    const valueLabel = apiFailureSignature(rawValue) ?? `[${type}]`
    return sanitizeAnalyticsObject({
      ...exception,
      type,
      value: valueLabel,
    })
  })
}

function sanitizeAnalyticsObject(
  properties: Record<string, any>,
  preservePosthogProjectToken = false
): Record<string, any> {
  for (const key of Object.keys(properties)) {
    if (AUTOCAPTURE_PAYLOAD_KEYS.has(key)) {
      delete properties[key]
      continue
    }
    if (key === "$exception_list") {
      properties[key] = sanitizeExceptionList(properties[key])
      continue
    }
    if (
      // The SDK's public project token is required on the top-level event
      // envelope. Nested candidate/application tokens must still be removed.
      !(preservePosthogProjectToken && key === "token") &&
      !SAFE_DERIVED_KEYS.has(key) &&
      SENSITIVE_ANALYTICS_KEY_RE.test(key)
    ) {
      delete properties[key]
      continue
    }

    let value = properties[key]
    // A raw stack is multi-line, so sanitizeStringValue would always redact
    // it. Keep the first frames on one line, under the length cap.
    if (key === "error_stack" && typeof value === "string") {
      value = value.split("\n").map((line) => line.trim()).join(" | ").slice(0, 160)
    }
    if (typeof value === "string") {
      properties[key] = sanitizeStringValue(value)
    } else if (Array.isArray(value)) {
      properties[key] = value.map((item) =>
        typeof item === "string"
          ? sanitizeStringValue(item)
          : item && typeof item === "object"
          ? sanitizeAnalyticsObject(item as Record<string, any>)
          : item
      )
    } else if (value && typeof value === "object") {
      properties[key] = sanitizeAnalyticsObject(value as Record<string, any>)
    }
  }
  return properties
}

function sanitizePosthogPropertiesInternal(
  properties: Record<string, any>,
  preservePosthogProjectToken = false
): Record<string, any> {
  // Public survey links include a long-lived application token in the query
  // string. PostHog extracts campaign properties separately, so origin + path
  // retains route analytics without storing credentials or candidate ids.
  for (const key of POSTHOG_URL_PROPERTIES) {
    if (typeof properties[key] === "string") {
      properties[key] = privacySafeUrl(
        properties[key],
        POSTHOG_EXTERNAL_URL_PROPERTIES.has(key)
      )
    }
  }
  for (const key of Object.keys(properties)) {
    if (isRawCampaignProperty(key)) delete properties[key]
  }
  for (const nestedKey of ["$set", "$set_once"]) {
    const nested = properties[nestedKey]
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      sanitizePosthogPropertiesInternal(nested)
    }
  }
  return sanitizeAnalyticsObject(properties, preservePosthogProjectToken)
}

export function sanitizePosthogProperties(
  properties: Record<string, any>
): Record<string, any> {
  return sanitizePosthogPropertiesInternal(properties)
}

export const sanitizeAndFilterPosthogEvent: BeforeSendFn = (event) => {
  const filtered = filterPosthogEvent(event)
  // The replay recorder puts live SDK objects in its snapshots by reference
  // (a "$posthog_config" custom event carries `posthog.config` itself). The
  // sanitizer below edits in place, so without a copy it deleted
  // `config.token` and every later event in the session was rejected (HIR-390).
  if (filtered?.event === "$snapshot" && filtered.properties?.$snapshot_data) {
    filtered.properties.$snapshot_data = JSON.parse(
      JSON.stringify(filtered.properties.$snapshot_data)
    )
  }
  if (filtered?.properties) {
    sanitizePosthogPropertiesInternal(filtered.properties, true)
    // Group API failures by endpoint instead of by stack, so one fault is one
    // issue across deploys. Read after sanitization: the exception value is now
    // the normalized `[<status> <path>]` label.
    const fingerprint = apiFailureFingerprint(filtered.properties.$exception_list)
    if (fingerprint) {
      filtered.properties.$exception_fingerprint = fingerprint
    }
  }
  if (filtered?.$set) sanitizePosthogProperties(filtered.$set)
  if (filtered?.$set_once) sanitizePosthogProperties(filtered.$set_once)
  return filtered
}

export function maskPosthogRecordingRequest(
  request: CapturedNetworkRequest
): CapturedNetworkRequest {
  const sanitized = { ...request }
  sanitized.name = privacySafeUrl(request.name)
  delete sanitized.requestBody
  delete sanitized.responseBody
  delete sanitized.requestHeaders
  delete sanitized.responseHeaders
  return sanitized
}

export function isPrivacySafeReplayRoute(pathname: string): boolean {
  return [
    /^\/survey\/[^/]+/,
    /^\/jobs\/[^/]+\/[^/]+/,
    /^\/dashboard\/candidates\/[^/]+/,
    /^\/dashboard\/(?:ha-team\/)?messages(?:\/|$)/,
  ].some((pattern) => pattern.test(pathname))
}

function privacySafeReplaySample(): boolean {
  try {
    const stored = window.sessionStorage.getItem(PRIVACY_REPLAY_SAMPLE_KEY)
    if (stored === "1") return true
    if (stored === "0") return false
    const sampled = Math.random() < PRIVACY_REPLAY_SAMPLE_RATE
    window.sessionStorage.setItem(
      PRIVACY_REPLAY_SAMPLE_KEY,
      sampled ? "1" : "0"
    )
    return sampled
  } catch {
    return false
  }
}

type RecordingClient = Pick<
  typeof posthog,
  "sessionRecordingStarted" | "startSessionRecording" | "stopSessionRecording"
> & { __loaded?: boolean }

export function syncPrivacySafeSessionRecording(
  client: RecordingClient,
  pathname: string,
  sampled: boolean
) {
  if (!client.__loaded) return
  const shouldRecord = sampled && isPrivacySafeReplayRoute(pathname)
  const isRecording = client.sessionRecordingStarted()
  if (shouldRecord && !isRecording) {
    client.startSessionRecording({ sampling: true, linked_flag: true })
  } else if (!shouldRecord && isRecording) {
    client.stopSessionRecording()
  }
}

export const POSTHOG_PRIVACY_CONFIG = {
  autocapture: false,
  rageclick: false,
  mask_all_text: true,
  mask_all_element_attributes: true,
  mask_personal_data_properties: true,
  custom_personal_data_properties: [
    "app",
    "application_id",
    "candidate",
    "candidate_id",
    "email",
    "exp",
    "name",
    "phone",
    "search",
    "token",
  ],
  disable_session_recording: true,
  enable_recording_console_log: false,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: "*",
    blockSelector: "iframe, img, video, audio, canvas, [data-ph-block]",
    recordCrossOriginIframes: false,
    recordCanvas: false,
    collectFonts: false,
    maskCapturedNetworkRequestFn: maskPosthogRecordingRequest,
    recordBody: false,
    recordHeaders: false,
  },
}

// `capture_pageview: true` records hard page loads only. App Router <Link>
// navigations never reload, so posthog-js must watch the history API instead;
// "history_change" fires on pathname changes, not on query-only updates.
export const POSTHOG_PAGEVIEW_CONFIG = {
  capture_pageview: "history_change",
  capture_pageleave: true,
} as const

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST

    if (key && host && typeof window !== "undefined") {
      posthog.init(key, {
        api_host: host,
        person_profiles: "identified_only",
        ...POSTHOG_PRIVACY_CONFIG,
        ...POSTHOG_PAGEVIEW_CONFIG,
        capture_exceptions: true,
        before_send: sanitizeAndFilterPosthogEvent,
        loaded: (client) =>
          syncPrivacySafeSessionRecording(
            client as RecordingClient,
            window.location.pathname,
            privacySafeReplaySample()
          ),
      })
    }
  }, [])

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return
    syncPrivacySafeSessionRecording(
      posthog as RecordingClient,
      pathname,
      privacySafeReplaySample()
    )
  }, [pathname])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
